import React, { Fragment, MutableRefObject } from "react";
import Helmet from "react-helmet";
import {
  Text,
  Notice,
  Kit,
  KitVersion,
  Item,
  ActivityIndicator,
  PagingIndicator,
  EmptyState,
} from "@thenounproject/lingo-core";

import GalleryItems from "./GalleryItems";

import { useViewModeContext } from "@contexts/ViewModeProvider";
import useRecentlyDeletedItems from "@redux/actions/items/useRecentlyDeletedItems";
import useUpdateItem from "@redux/actions/items/useUpdateItem";

type Props = {
  // Passed
  kit: Kit;
  kitVersion: KitVersion;
  getItemUrl: (item: Item) => string;
  onSelectItem: (event: React.MouseEvent, itemId: string, items: Item[]) => void;
  setInspectorState: (itemIds: string[]) => void;
  selectedItems: string[];
  scrollContainerRef: MutableRefObject<HTMLDivElement>;
  canEditContent: boolean;
};

const KitRecentlyDeletedItems: React.FC<Props> = ({
  kit,
  kitVersion,
  getItemUrl,
  onSelectItem,
  setInspectorState,
  selectedItems,
  scrollContainerRef,
  canEditContent,
}) => {
  const [updateItem] = useUpdateItem();
  const {
    data: items,
    isLoading,
    fetchNextPage,
    error: loadingError,
  } = useRecentlyDeletedItems({
    kitId: kit.kitId,
    spaceId: kit.spaceId,
  });

  const trashedItemCount = kitVersion.counts.trash;
  const hasMoreAssets = items?.length >= trashedItemCount ? false : true;
  const { galleryAssetViewMode } = useViewModeContext();

  function renderContent() {
    if (trashedItemCount === 0) {
      return (
        <EmptyState
          title="No assets have been removed recently"
          subtitle="Click the ‘Done’ button to exit the Recently Removed section."
          iconProps={{ iconId: "trash", size: "60", fill: "gray" }}
        />
      );
    }
    if (isLoading && !items?.length) {
      return <ActivityIndicator mt="xxl" center />;
    }
    if (loadingError && !items?.length) {
      return (
        <EmptyState
          title="Unable to load recently removed assets"
          subtitle={loadingError.message}
          iconProps={{ iconId: "error", fill: "error" }}
          button={{ text: "Retry", onClick: fetchNextPage }}
        />
      );
    }
    return (
      <>
        <GalleryItems
          items={items}
          getItemUrl={getItemUrl}
          onSelectItem={onSelectItem}
          setInspectorState={setInspectorState}
          selectedItems={selectedItems}
          scrollContainerRef={scrollContainerRef}
          canEditContent={canEditContent}
          updateItem={updateItem}
          assetViewMode={galleryAssetViewMode}
        />

        <PagingIndicator
          hasMore={hasMoreAssets}
          data={items}
          error={loadingError}
          fetchNextPage={fetchNextPage}
          isLoading={isLoading}
        />
      </>
    );
  }

  return (
    <Fragment>
      <Helmet title={`${kit.name}: Recently Removed`} />
      <Text as="h1" font="gallery.title" mb="xl">
        Recently Removed
      </Text>
      <Notice
        message="Assets shown here will be permanently removed from this Kit after 30 days."
        mb="xl"
      />

      {renderContent()}
    </Fragment>
  );
};

export default KitRecentlyDeletedItems;
